/**
 * 인증 상태 관리 스토어
 * 로그인, 회원가입, 소셜 로그인, 로그아웃, 프로필 관리
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User, AuthState } from '@/types'
import api, { ApiError } from '@/lib/api'

/** 로그인 응답 타입 */
interface LoginResponse {
  user: User
  token: string
  refreshToken?: string
}

/** 회원가입 응답 타입 */
interface SignupResponse {
  user: User
  token: string
  refreshToken?: string
}

interface AuthActions {
  /** 이메일/비밀번호 로그인 */
  login: (email: string, password: string) => Promise<void>
  /** 회원가입 */
  signup: (name: string, email: string, password: string) => Promise<void>
  /** 소셜 로그인 (카카오/구글) */
  socialLogin: (provider: 'kakao' | 'google') => void
  /** 로그아웃 */
  logout: () => void
  /** 현재 사용자 정보 갱신 */
  fetchCurrentUser: () => Promise<void>
  /** 프로필 업데이트 */
  updateProfile: (data: { name?: string; avatarUrl?: string }) => Promise<void>
  /** 사용자 직접 설정 */
  setUser: (user: User) => void
  /** 토큰 직접 설정 */
  setToken: (token: string) => void
  /** 에러 설정 */
  setError: (error: string | null) => void
  /** 에러 초기화 */
  clearError: () => void
}

/** persist 하이드레이션 완료 플래그를 포함한 스토어 타입 */
type AuthStore = AuthState &
  AuthActions & {
    /** Zustand persist 리하이드레이션 완료 여부 (새로고침 시 오인 리다이렉트 방지) */
    _hasHydrated: boolean
    /** 하이드레이션 완료 표시 */
    setHasHydrated: (v: boolean) => void
  }

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // 초기 상태
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      // 하이드레이션 완료 전에는 false → 가드가 인증 판단을 미룸
      _hasHydrated: false,

      // 하이드레이션 완료 표시
      setHasHydrated: (v: boolean) => set({ _hasHydrated: v }),

      // 이메일/비밀번호 로그인
      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null })
        try {
          const response = await api.post<LoginResponse>('/auth/login', {
            email,
            password,
          })

          set({
            user: response.user,
            token: response.token,
            refreshToken: response.refreshToken || null,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          })
        } catch (error) {
          const message =
            error instanceof ApiError
              ? error.message
              : '로그인에 실패했습니다. 다시 시도해주세요.'
          set({
            isLoading: false,
            error: message,
          })
          throw error
        }
      },

      // 회원가입
      signup: async (name: string, email: string, password: string) => {
        set({ isLoading: true, error: null })
        try {
          const response = await api.post<SignupResponse>('/auth/register', {
            // 백엔드 /auth/register는 req.body.nickname을 읽으므로 name을 nickname으로 전송
            nickname: name,
            email,
            password,
          })

          set({
            user: response.user,
            token: response.token,
            refreshToken: response.refreshToken || null,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          })
        } catch (error) {
          const message =
            error instanceof ApiError
              ? error.message
              : '회원가입에 실패했습니다. 다시 시도해주세요.'
          set({
            isLoading: false,
            error: message,
          })
          throw error
        }
      },

      // 소셜 로그인 (카카오/구글) - OAuth 리다이렉트 방식
      socialLogin: (provider: 'kakao' | 'google') => {
        const baseUrl = process.env.NEXT_PUBLIC_API_URL || '/api'
        window.location.href = `${baseUrl}/auth/${provider}`
      },

      // 로그아웃
      logout: () => {
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        })
      },

      // 현재 사용자 정보 갱신
      fetchCurrentUser: async () => {
        const { token } = get()
        if (!token) return

        try {
          const user = await api.get<User>('/auth/me')
          set({ user, isAuthenticated: true })
        } catch {
          // 토큰이 유효하지 않으면 로그아웃 처리
          set({
            user: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false,
          })
        }
      },

      // 프로필 업데이트
      updateProfile: async (data) => {
        set({ isLoading: true, error: null })
        try {
          // 백엔드 PUT /auth/me 는 { nickname, profileImage } 를 받고 { message, user } 를 반환
          const payload: { nickname?: string; profileImage?: string } = {}
          if (data.name !== undefined) payload.nickname = data.name
          if (data.avatarUrl !== undefined) payload.profileImage = data.avatarUrl
          const res = await api.put<{ message: string; user: User }>(
            '/auth/me',
            payload
          )
          set({ user: res.user, isLoading: false })
        } catch (error) {
          const message =
            error instanceof ApiError
              ? error.message
              : '프로필 업데이트에 실패했습니다.'
          set({ isLoading: false, error: message })
        }
      },

      // 사용자 직접 설정
      setUser: (user: User) => {
        set({ user, isAuthenticated: true })
      },

      // 토큰 직접 설정
      setToken: (token: string) => {
        set({ token })
      },

      // 에러 설정
      setError: (error: string | null) => {
        set({ error })
      },

      // 에러 초기화
      clearError: () => {
        set({ error: null })
      },
    }),
    {
      name: 'video-editor-auth',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
      // localStorage 리하이드레이션 완료 시 플래그 설정 (비동기 완료 시점)
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true)
      },
    }
  )
)
